import { Badge, Button, Text } from "@fluentui/react-components";

import {
  $borderForeground1,
  $greenStatus,
  $primaryHoveredForeground,
  $softInfoBackground,
} from "../../../cssjs/colors";
import { toSentenceCase } from "../../../utils/common/CommonHelperFunctions";
import {
  getNotificationCategoryIcon,
  getNotificationCategoryLabel,
  getSeverityColor,
} from "../../../utils/notification/NotificationPanelHelperFunctions";
import { NotificationItem } from "../types";

import { useNotificationCardStyles } from "./styles";

interface NotificationCardProps {
  notification: NotificationItem;
  expanded: boolean;
  onExpandedChange: (notificationId: string) => void;
}

const NotificationHistoryCard: React.FC<NotificationCardProps> = ({
  notification,
  expanded,
  onExpandedChange,
}) => {
  const styles = useNotificationCardStyles();
  const {
    id,
    title,
    body,
    severity,
    category,
    timestamp,
    updatedTimestamp,
    start,
    end,
  } = notification;

  const [notificationDate, notificationTime] = timestamp.split(" ");
  const formattedStart = start;
  const formattedEnd = end;

  const hasBody = body.length > 0;
  const categoryIcon = getNotificationCategoryIcon(category);
  const categoryLabel = getNotificationCategoryLabel(category);

  const detailsId = `notification-details-${id}`;

  return (
    <article className={styles.card}>
      <div className={styles.cardRow}>
        <div className={`${styles.cell} ${styles.time}`}>
          <Text block>{notificationDate}</Text>
          <Text block>{notificationTime}</Text>
          {updatedTimestamp && (
            <div style={{ color: $greenStatus }}>
              <Text as="span" block size={200} weight="medium">
                Updated:
              </Text>
              <Text as="span" block size={200} weight="medium">
                {updatedTimestamp}
              </Text>
            </div>
          )}
        </div>

        <div className={`${styles.cell} ${styles.alert}`}>
          {categoryIcon ? (
            <span className={styles.alertIcon}>
              <img
                src={categoryIcon}
                alt={`${category} icon`}
                aria-hidden="true"
                className={styles.alertIconImage}
              />
            </span>
          ) : null}

          <div>
            <Text className={styles.alertTitle} weight="semibold">
              {title}
            </Text>
          </div>
        </div>

        <div className={styles.cell}>
          <Badge
            appearance="filled"
            shape="circular"
            color={getSeverityColor(severity)}
            className={styles.severityBadge}
            style={{ padding: "12px" }}
          >
            {toSentenceCase(severity)}
          </Badge>
        </div>

        <div className={styles.cell}>
          <Badge
            appearance="outline"
            shape="circular"
            className={styles.categoryTag}
            style={{ padding: "12px", color: $borderForeground1 }}
          >
            {categoryLabel}
          </Badge>
        </div>

        <div className={`${styles.cell} ${styles.messageCell}`}>
          {hasBody ? (
            <Text className={styles.messagePreview} title={body}>
              {body}
            </Text>
          ) : (
            <Text className={styles.emptyValue}>No message available</Text>
          )}
        </div>

        <div className={`${styles.cell}`}>
          <ImpactPeriod start={formattedStart} end={formattedEnd} />
        </div>

        <div className={`${styles.cell}`}>
          {hasBody && (
            <Button
              appearance="subtle"
              className={styles.showHideButton}
              style={{
                backgroundColor: expanded ? $softInfoBackground : undefined,
                color: expanded ? $primaryHoveredForeground : undefined,
              }}
              aria-expanded={expanded}
              aria-controls={detailsId}
              onClick={() => {
                onExpandedChange(id);
              }}
            >
              {expanded ? "Hide details" : "Show details"}
            </Button>
          )}
        </div>
      </div>

      {expanded && hasBody && (
        <div
          id={detailsId}
          className={styles.details}
          role="region"
          aria-label={`${title} details`}
        >
          <div className={styles.detailsContent}>
            <Text className={styles.detailsText}>{body}</Text>
          </div>
        </div>
      )}
    </article>
  );
};

export default NotificationHistoryCard;

interface ImpactPeriodProps {
  start: string;
  end: string;
}

const ImpactPeriod: React.FC<ImpactPeriodProps> = ({ start, end }) => {
  const styles = useNotificationCardStyles();

  return (
    <div className={styles.impact}>
      {start && <Text block>{start}</Text>}
      {end && <Text block>{start ? `- ${end}` : end}</Text>}
    </div>
  );
};
